import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PickerActions, PickerWrapper, RangeDatePickerWrapper } from './styles'
import Button from 'components/Button'
import { CalendarIcon } from 'components/Icons'
import { DateRangePicker, Range, StaticRange } from 'react-date-range'
import { isSameDay, sub } from 'date-fns'
import i18n, { TFunction } from 'i18next'
import { enUS, fr } from 'date-fns/locale'
import colors from 'globals/colors'
import { SetState } from 'globals'


const todayRange = (t: TFunction): StaticRange => ({
  label: t('date-range.today'),
  range: () => ({
    startDate: new Date(),
    endDate: new Date()
  }),
  isSelected: rangeIsEqual(new Date(), new Date())
})

const yesterdayRange = (t: TFunction): StaticRange => ({
  label: t('date-range.yesterday'),
  range: () => ({
    startDate: sub(new Date(), { days: 1 }),
    endDate: sub(new Date(), { days: 1 })
  }),
  isSelected: rangeIsEqual(sub(new Date(), { days: 1 }), sub(new Date(), { days: 1 }))
})

const lastWeekRange = (t: TFunction): StaticRange => ({
  label: t('date-range.7-last'),
  range: () => ({
    startDate: sub(new Date(), { days: 7 }),
    endDate: new Date()
  }),
  isSelected: rangeIsEqual(sub(new Date(), { days: 7 }), new Date())
})

const lastMonthRange = (t: TFunction): StaticRange => ({
  label: t('date-range.30-last'),
  range: () => ({
    startDate: sub(new Date(), { days: 30 }),
    endDate: new Date()
  }),
  isSelected: rangeIsEqual(sub(new Date(), { days: 30 }), new Date())
})

const lastTrimesterRange = (t: TFunction): StaticRange => ({
  label: t('date-range.90-last'),
  range: () => ({
    startDate: sub(new Date(), { months: 3 }),
    endDate: new Date()
  }),
  isSelected: rangeIsEqual(sub(new Date(), { months: 3 }), new Date())
})

const rangeIsEqual = (startDate: Date, endDate: Date) => (range: Range): boolean => {
  return (
    (range.startDate &&
      range.endDate &&
      isSameDay(startDate, range.startDate) &&
      isSameDay(endDate, range.endDate)
    ) ||
    false
  )
}

const locales: { [key: string]: Locale } = {
  en: enUS,
  fr: fr
}


export interface DateRange {
  startDate: Date,
  endDate: Date
}

interface IProps {
  onValidateRange: (range: DateRange) => void
}

const RangeDatePicker = (props: IProps) => {
  const { onValidateRange } = props
  const { t } = useTranslation()
  const [showPicker, setShowPicker] = useState(false)
  const [range, setRange] = useState<Range>({
    startDate: null as any,
    endDate: new Date(''),
    color: colors.waterBlue
  })

  return (
    <RangeDatePickerWrapper>
      <Button
        onClick={() => {
          setShowPicker((showPicker) => !showPicker)
        }}
      >
        <CalendarIcon />
        <span>{t('global.date')}</span>
      </Button>

      <PickerWrapper visible={showPicker}>
        <DateRangePicker
          ranges={range ? [range] : []}
          onChange={(selectedRange) => setRange((selectedRange as any).range1)}
          months={2}
          direction="horizontal"
          staticRanges={[
            todayRange(t),
            yesterdayRange(t),
            lastWeekRange(t),
            lastMonthRange(t),
            lastTrimesterRange(t)
          ]}
          inputRanges={[]}
          locale={locales[i18n.language]}
          rangeColors={[colors.waterBlue]}
        />

        <PickerActions>
          <Button
            size="small"
            disabled={range.startDate === null}
            onClick={eraseSelection(setRange)}
            alternativestyle
          >
            {t('global.erase')}
          </Button>

          <Button
            size="small"
            disabled={range.startDate === null}
            onClick={validateSelection(range, onValidateRange, setShowPicker)}
          >
            {t('global.validate')}
          </Button>
        </PickerActions>
      </PickerWrapper>
    </RangeDatePickerWrapper>
  )
}

const validateSelection = (
    range: Range,
    onValidateRange: (range: DateRange) => void,
    setShowPicker: SetState<boolean>
) => () => {
  if (range.startDate && range.endDate) {
    onValidateRange({
      startDate: range.startDate,
      endDate: range.endDate
    })

    setShowPicker(false)
  }
}

const eraseSelection = (setRange: SetState<Range>) => () =>
  setRange({
    startDate: null as any,
    endDate: new Date(''),
    color: colors.waterBlue
  })

export default RangeDatePicker
